import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoIcon from "@mui/icons-material/Info";

const ViewErrorLogDetailsModal = ({ open, onClose, data }) => {
  if (!data || !data.data) return null;

  const errorLog = data.data;

  const getSeverityColor = (severity) => {
    const colors = {
      WARNING: { bg: "#FFF3E0", color: "#E65100" },
      ERROR: { bg: "#FFEBEE", color: "#C62828" },
      CRITICAL: { bg: "#C62828", color: "#FFFFFF" },
    };
    return colors[severity] || { bg: "#F5F5F5", color: "#666" };
  };

  const severityColors = getSeverityColor(errorLog?.severity);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ErrorIcon color="error" />
            <Typography variant="h6" fontWeight={600}>
              Error Log Details
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Error Information Section */}
          <Card sx={{ backgroundColor: "#F9FAFB", border: "1px solid #E0E0E0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <ErrorIcon color="error" />
                <Typography variant="h6" fontWeight={600}>
                  Error Information
                </Typography>
                <Chip
                  label={errorLog?.severity || "ERROR"}
                  size="small"
                  sx={{
                    backgroundColor: severityColors.bg,
                    color: severityColors.color,
                    fontWeight: 600,
                    ml: 1,
                  }}
                />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InfoRow label="Error ID" value={errorLog?._id} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Error Module" value={errorLog?.error_module || "N/A"} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Error Message" value={errorLog?.error_message || "N/A"} />
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Error Details:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        backgroundColor: "#FFFFFF",
                        p: 2,
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: 12,
                        border: "1px solid #E0E0E0",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: 200,
                        overflow: "auto",
                      }}
                    >
                      {errorLog?.error_details || "No details available"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Timestamp Information */}
          <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <AccessTimeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Timestamp Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <InfoRow
                    label="Timestamp"
                    value={errorLog?.timestamp ? new Date(errorLog.timestamp).toLocaleString() : "N/A"}
                    icon={<AccessTimeIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow
                    label="Created At"
                    value={errorLog?.created_at ? new Date(errorLog.created_at).toLocaleString() : "N/A"}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow
                    label="Updated At"
                    value={errorLog?.updated_at ? new Date(errorLog.updated_at).toLocaleString() : "N/A"}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <InfoIcon color="info" />
                <Typography variant="h6" fontWeight={600}>
                  Additional Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <InfoRow label="Company ID" value={errorLog?.company_id || "N/A"} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="User ID" value={errorLog?.user_id || "N/A"} />
                </Grid>
                {errorLog?.actions && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Actions:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          backgroundColor: "#F5F5F5",
                          p: 1.5,
                          borderRadius: 1,
                          fontFamily: "monospace",
                          fontSize: 12,
                        }}
                      >
                        {JSON.stringify(errorLog.actions, null, 2)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper component for info rows
const InfoRow = ({ label, value, icon }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" gutterBottom>
      {label}:
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {icon}
      <Typography variant="body2" fontWeight={500}>
        {value || "N/A"}
      </Typography>
    </Box>
  </Box>
);

export default ViewErrorLogDetailsModal;
